"use client";

import { useRef, useState, type FormEvent } from "react";
import {
  platform,
  type DesignWebsite,
  type DesignCustomerActivation,
} from "./api";
import {
  Button,
  Input,
  Dialog,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from "./ui";
import { WebsiteCustomerInvitation } from "./WebsiteCustomerInvitation";
import css from "./BuildyWorkspace.module.css";

/** Operator-only ownership setup. The API enforces agency/admin access independently. */
export function WebsiteCustomerActivation({
  site,
  org,
  csrf,
  onActivated,
}: {
  site: DesignWebsite;
  org: number;
  csrf: string;
  onActivated?: (organizationId: number) => void;
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(site.name);
  const [email, setEmail] = useState("");
  const [subscription, setSubscription] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [customerOrg, setCustomerOrg] = useState(site.customer_organization_id);
  const lock = useRef(false);
  const intent = useRef<{ signature: string; key: string } | null>(null);
  async function activate(event: FormEvent) {
    event.preventDefault();
    if (lock.current || !site.live_release) return;
    lock.current = true;
    setBusy(true);
    setError("");
    const body = {
      expected_source_version: site.source_version,
      approved_release_id: site.live_release,
      customer_name: name.trim(),
      customer_email: email.trim().toLowerCase(),
      ...(subscription.trim()
        ? { hosting_subscription_id: Number(subscription) }
        : {}),
    };
    const signature = JSON.stringify(body);
    if (intent.current?.signature !== signature)
      intent.current = { signature, key: crypto.randomUUID() };
    try {
      const result = await platform<DesignCustomerActivation>(
        `websites/${site.id}/activate-customer/`,
        {
          method: "POST",
          org,
          csrf,
          body: { ...body, request_key: intent.current.key },
        },
      );
      setCustomerOrg(result.organization_id);
      onActivated?.(result.organization_id);
      setOpen(false);
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "Could not activate customer access.",
      );
    } finally {
      lock.current = false;
      setBusy(false);
    }
  }
  return (
    <section className={css.panel}>
      <div className={css.panelHeading}>
        <h2>Customer access</h2>
      </div>
      <div className={css.panelBody}>
        {customerOrg ? (
          <p role="status">
            Connected to customer organization {customerOrg}. Agency access is
            retained.
          </p>
        ) : (
          <>
            <p>
              Connect {site.name} to its customer account after approval and
              hosting setup.
            </p>
            <Button
              disabled={!site.live_release}
              onClick={() => {
                setError("");
                setOpen(true);
              }}
            >
              Activate customer
            </Button>
          </>
        )}
      </div>
      {customerOrg && (
        <div className={css.panelBody}>
          <WebsiteCustomerInvitation
            websiteId={site.id}
            org={org}
            csrf={csrf}
          />
        </div>
      )}
      {open && (
        <Dialog
          ariaLabel="Activate customer access"
          onClose={() => {
            if (!busy) setOpen(false);
          }}
          closeOnEscape={!busy}
        >
          <form onSubmit={(event) => void activate(event)}>
            <DialogHeader>
              <DialogTitle>Activate customer access</DialogTitle>
            </DialogHeader>
            <DialogBody>
              <p>
                The existing website moves to the customer’s organization. Its
                source, history and hosting stay connected, and your agency
                keeps management access.
              </p>
              <div className={css.activationFields}>
                <label>
                  Business name
                  <Input
                    required
                    maxLength={160}
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    disabled={busy}
                  />
                </label>
                <label>
                  Customer email
                  <Input
                    type="email"
                    required
                    maxLength={255}
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    disabled={busy}
                  />
                </label>
                <label>
                  Hosting subscription ID (if not linked)
                  <Input
                    type="number"
                    min={1}
                    step={1}
                    value={subscription}
                    onChange={(event) => setSubscription(event.target.value)}
                    disabled={busy}
                  />
                </label>
              </div>
              <p>
                An existing account is reused when available. This step does not
                send an invitation.
              </p>
              {error && (
                <p role="alert" className={css.error}>
                  {error}
                </p>
              )}
            </DialogBody>
            <DialogFooter>
              <Button disabled={busy} onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={busy || !name.trim() || !email.trim()}
              >
                {busy ? "Activating…" : "Activate customer"}
              </Button>
            </DialogFooter>
          </form>
        </Dialog>
      )}
    </section>
  );
}
